import React, { useReducer, createContext, useContext, memo, useState, useRef, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { fontStyle, spacing, themeLight } from '@naf/theme';
import { propValidationConfig } from '../propValidationConfig';

// TODO: Fix Typescript and a11y

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      return {
        ...state,
        selectedTab: action.payload
      };
    default:
      return state;
  }
};
export const TabsContext = /*#__PURE__*/createContext({
  selectedTab: ''
});

// TABS

export const Tabs = /*#__PURE__*/memo(({
  children,
  selectedTab,
  maxWidth,
  className
}) => {
  const [state, dispatch] = useReducer(reducer, {
    selectedTab
  });
  useEffect(() => {
    dispatch({
      type: 'SET_ACTIVE_TAB',
      payload: selectedTab
    });
  }, [selectedTab]);
  const value = {
    state,
    dispatch
  };
  return /*#__PURE__*/React.createElement(TabsContext.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement(StyledTabs, {
    maxWidth: maxWidth,
    className: className
  }, children));
});
const StyledTabs = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "tabs__StyledTabs",
  componentId: "sc-373pnz-0"
})(["max-width:", "px;"], ({
  maxWidth
}) => maxWidth);

// TABS LIST

export const TabList = /*#__PURE__*/memo(({
  children
}) => {
  const ref = useRef(null);
  const [hasOverflowLeft, setHasOverflowLeft] = useState(false);
  const [hasOverflowRight, setHasOverflowRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const handleResize = useCallback(() => {
    const node = ref.current;
    if (node.scrollLeft > 0) {
      setHasOverflowLeft(true);
    } else {
      setHasOverflowLeft(false);
    }
    if (node.scrollLeft < node.scrollWidth - node.clientWidth) {
      setHasOverflowRight(true);
    } else {
      setHasOverflowRight(false);
    }
  }, []);
  useEffect(() => {
    handleResize();
  }, [handleResize]);
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);
  useEffect(() => {
    const node = ref.current;
    node.addEventListener('scroll', handleResize);
    return () => node.removeEventListener('scroll', handleResize);
  }, [handleResize]);

  // Allow dragging of the tabs on desktop.
  const handleMouseDown = e => {
    setIsDragging(true);
    setStartX(e.pageX - (ref.current?.offsetLeft || 0));
    setScrollLeft(ref.current?.scrollLeft || 0);
  };
  const handleMouseMove = e => {
    if (!isDragging) return;
    const node = ref.current;
    if (!node) return;
    const x = e.pageX - (node.offsetLeft || 0);
    const walk = (x - startX) * 1;
    node.scrollLeft = scrollLeft - walk;
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  return /*#__PURE__*/React.createElement(StyledTabList, {
    role: "tablist"
  }, /*#__PURE__*/React.createElement(ScrollView, {
    ref: ref,
    onMouseDown: handleMouseDown,
    onMouseLeave: handleMouseUp,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp
  }, children), /*#__PURE__*/React.createElement(ScrollIndicator, {
    left: true,
    visible: hasOverflowLeft
  }), /*#__PURE__*/React.createElement(ScrollIndicator, {
    right: true,
    visible: hasOverflowRight
  }));
});
const ScrollIndicator = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "tabs__ScrollIndicator",
  componentId: "sc-373pnz-1"
})(["position:absolute;top:0;right:0;height:100%;width:80px;pointer-events:none;z-index:2;opacity:", ";transition:opacity 0.3s ease;", " ", ""], ({
  visible
}) => visible ? 1 : 0, ({
  left
}) => left && css(["left:0;background:linear-gradient(to right,white 1%,rgba(255,255,255,0) 100%);"]), ({
  right
}) => right && css(["right:0;background:linear-gradient(to left,white 1%,rgba(255,255,255,0) 100%);"]));
const ScrollView = styled.div.withConfig({
  displayName: "tabs__ScrollView",
  componentId: "sc-373pnz-2"
})(["display:flex;overflow:auto;white-space:nowrap;padding-left:2px;cursor:", ";&::-webkit-scrollbar{display:none;}"], ({
  isDragging
}) => isDragging ? 'grabbing' : 'grab');
const StyledTabList = styled.div.withConfig({
  displayName: "tabs__StyledTabList",
  componentId: "sc-373pnz-3"
})(["position:relative;margin-bottom:", ";&:after{content:'';display:block;width:100%;margin-left:2px;box-sizing:border-box;height:1px;background-color:rgba(0,0,0,0.12);}"], spacing.space24);

// TAB

export const Tab = /*#__PURE__*/memo(({
  children,
  disabled = false,
  item
}) => {
  const {
    dispatch,
    state
  } = useContext(TabsContext);
  const [mouseDown, setMouseDown] = useState(false);
  const removeOutline = () => {
    setMouseDown(true);
  };
  const addOutline = e => {
    if (e.key === 'Tab') {
      setMouseDown(false);
    }
  };
  useEffect(() => {
    window.addEventListener('mousedown', removeOutline);
    return () => window.removeEventListener('mousedown', removeOutline);
  }, []);
  useEffect(() => {
    window.addEventListener('keydown', addOutline);
    return () => window.removeEventListener('keydown', addOutline);
  }, []);
  const selected = state.selectedTab === item;
  return /*#__PURE__*/React.createElement(StyledTab, {
    role: "tab",
    id: `tab_${item}`,
    "aria-controls": `tabpanel_${item}`,
    "aria-selected": selected,
    onClick: () => dispatch({
      type: 'SET_ACTIVE_TAB',
      payload: item
    }),
    className: mouseDown ? 'mousedown' : '',
    disabled: disabled
  }, children);
});
const StyledTab = styled.button.withConfig({
  displayName: "tabs__StyledTab",
  componentId: "sc-373pnz-4"
})(["border:none;margin:0;padding:0;width:auto;overflow:visible;background:transparent;color:inherit;font:inherit;line-height:normal;appearance:none;touch-action:manipulation;cursor:pointer;border-bottom:0px solid transparent;padding-bottom:", ";box-sizing:border-box;height:", ";display:inline-flex;", " font-weight:300;color:", ";&:hover{color:", ";}&[aria-selected='true']{font-weight:500;border-bottom-width:", ";border-color:", " !important;color:", ";&:hover,&:focus{color:", ";}}&:not(:last-child){margin-right:", ";}&:focus,&:focus-visible{margin-top:calc(", " * 0.5);height:calc(", " - ", ");line-height:calc(", " - ", ");&[aria-selected='true']{border-bottom-width:2px;}}&.mousedown{outline:0 !important;margin-top:0;height:", ";line-height:", ";&[aria-selected='true']{border-bottom-width:", ";border-radius:0 !important;}}", ""], spacing.space8, spacing.space40, fontStyle.headers.subHeader, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  theme
}) => theme.typography ? theme.typography.textLink : themeLight.typography.textLink, spacing.space4, ({
  theme
}) => theme.componentColors ? theme.componentColors.navigation.active : themeLight.componentColors.navigation.active, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, spacing.space24, spacing.space4, spacing.space40, spacing.space4, fontStyle.headers.subHeader['line-height'], spacing.space4, spacing.space40, fontStyle.headers.subHeader['line-height'], spacing.space4, ({
  disabled
}) => disabled && css(["cursor:not-allowed;color:", ";&:hover,&:focus{color:", ";}"], ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText));

// TAB PANEL

export const TabPanel = /*#__PURE__*/memo(({
  children,
  item
}) => {
  const {
    state
  } = useContext(TabsContext);
  const selected = state.selectedTab === item;
  const style = {};
  return /*#__PURE__*/React.createElement(StyledTabPanel, {
    role: "tabpanel",
    id: `tabpanel_${item}`,
    tabIndex: -1,
    "aria-labelledby": `tab_${item}`,
    hidden: !selected,
    style: style
  }, children);
});
const StyledTabPanel = styled.div.withConfig({
  displayName: "tabs__StyledTabPanel",
  componentId: "sc-373pnz-5"
})(["appearance:none;outline:0;"]);
export default Tabs;
//# sourceMappingURL=tabs.js.map